import type { FinalSummary } from '@myrewards/contracts';
import { formatMoney } from '../ui';

/**
 * One line of X1's Final summaries card (PRD §10.2(8), N2, APP-054 decision 15), from the final
 * summary the Executives get when an Executive Discretionary Bonus closes
 * (`GET /insights/final-summaries`, APP-072): the pool's cash before and after as of the close,
 * and how it was decided.
 */
export interface SummaryLine {
  id: string;
  displayId: string;
  /** The organization day it closed. */
  closedOn: string;
  requestedBy: string;
  people: number;
  cashCents: number;
  options: number;
  /** "Executive pool $92,000 → $88,500", or "Paid from Executive pool $3,500". */
  impact: string[];
  /** "Approved in the app by …" or "By the Executives, outside the app; recorded by …". */
  decision: string;
  canOpenRecord: boolean;
}

/** Awards closed before final summaries were kept have no snapshot (APP-072). */
export const IMPACT_NOT_RECORDED =
  'Pool impact wasn’t recorded for awards closed before final summaries were kept.';

/** How the Executives' decision was made and recorded, in N2's words. */
export function decisionLine(decision: FinalSummary['decision']): string {
  const proof = decision.proofAttached ? 'proof attached' : null;
  if (decision.inApp) {
    const who = decision.executive ?? decision.recordedBy;
    const by =
      who === null ? 'Approved in the app by an Executive' : `Approved in the app by ${who.name}`;
    return proof === null ? by : `${by}; ${proof}`;
  }
  const recorded =
    decision.recordedBy === null
      ? null
      : `recorded by ${decision.recordedBy.name}${decision.recordedFor === null ? '' : ` for ${decision.recordedFor.name}`}`;
  const after = [recorded, proof].filter((part): part is string => part !== null).join(', ');
  return after === ''
    ? 'By the Executives, outside the app'
    : `By the Executives, outside the app; ${after}`;
}

/** X1's line for one final summary. */
export function fromFinalSummary(summary: FinalSummary): SummaryLine {
  return {
    id: summary.award.id,
    displayId: summary.award.displayId,
    closedOn: summary.closedOn,
    requestedBy: summary.requestedBy.name,
    people: summary.totals.people,
    cashCents: summary.totals.cashCents,
    options: summary.totals.options,
    // No snapshot for awards closed before summaries were kept: say so, never a blank or a dash.
    impact:
      !summary.impactRecorded || summary.poolImpact.length === 0
        ? [IMPACT_NOT_RECORDED]
        : summary.poolImpact.map((p) =>
            p.left === null
              ? `Paid from ${p.pool.name} pool ${formatMoney(p.paid.cashCents)}`
              : `${p.pool.name} pool ${formatMoney(p.left.before.cashCents)} → ${formatMoney(p.left.after.cashCents)}`,
          ),
    decision: decisionLine(summary.decision),
    canOpenRecord: summary.canOpenRecord,
  };
}
